import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MetricsCard from './MetricsCard';

const PopupManagerPanel = () => {
  const [popups, setPopups] = useState([
    {
      id: 1,
      title: 'Summer Wedding Discount',
      type: 'promotional',
      trigger: 'page-load',
      delay: 3000,
      status: 'active',
      targetPages: ['homepage', 'vendor-listing'],
      content: 'Get 20% off on all summer wedding bookings!',
      cta: 'Claim Offer',
      impressions: 1450,
      conversions: 89,
      createdAt: '2025-07-10T10:00:00Z'
    },
    {
      id: 2,
      title: 'Profile Completion',
      type: 'informational',
      trigger: 'exit-intent',
      delay: 0,
      status: 'active',
      targetPages: ['user-dashboard'],
      content: 'Complete your profile to get better vendor recommendations',
      cta: 'Complete Profile',
      impressions: 890,
      conversions: 134,
      createdAt: '2025-07-08T14:30:00Z'
    },
    {
      id: 3,
      title: 'Newsletter Signup',
      type: 'subscription',
      trigger: 'time-based',
      delay: 30000,
      status: 'paused',
      targetPages: ['all'],
      content: 'Subscribe to our newsletter for wedding tips and exclusive offers',
      cta: 'Subscribe Now',
      impressions: 2340,
      conversions: 187,
      createdAt: '2025-07-05T09:15:00Z'
    }
  ]);

  const [newPopup, setNewPopup] = useState({
    title: '',
    type: 'promotional',
    trigger: 'page-load',
    delay: 3000,
    targetPages: ['homepage'],
    content: '',
    cta: 'Learn More',
    buttonColor: '#ff6b6b',
    backgroundColor: '#ffffff'
  });

  const popupStats = {
    totalPopups: popups.length,
    active: popups.filter(p => p.status === 'active').length,
    totalImpressions: popups.reduce((sum, p) => sum + p.impressions, 0),
    avgConversionRate: popups.reduce((sum, p) => sum + (p.conversions / p.impressions * 100), 0) / popups.length
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'paused': return 'text-yellow-600 bg-yellow-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'promotional': return 'text-purple-600 bg-purple-100';
      case 'informational': return 'text-blue-600 bg-blue-100';
      case 'subscription': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleStatusToggle = (popupId) => {
    setPopups(prev => prev.map(popup => 
      popup.id === popupId 
        ? { ...popup, status: popup.status === 'active' ? 'paused' : 'active' }
        : popup
    ));
  };

  const handleCreatePopup = () => {
    if (newPopup.title && newPopup.content) {
      const popup = {
        id: Date.now(),
        ...newPopup,
        status: 'draft',
        impressions: 0,
        conversions: 0,
        createdAt: new Date().toISOString()
      };
      setPopups(prev => [popup, ...prev]);
      setNewPopup({
        title: '',
        type: 'promotional',
        trigger: 'page-load',
        delay: 3000,
        targetPages: ['homepage'],
        content: '',
        cta: 'Learn More',
        buttonColor: '#ff6b6b',
        backgroundColor: '#ffffff'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Pop-up Manager
        </h2>
        <p className="text-indigo-100">
          Create and manage website pop-ups and notifications
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricsCard
          title="Total Pop-ups"
          value={popupStats.totalPopups}
          icon="MessageSquare"
          color="blue"
        />
        <MetricsCard
          title="Active"
          value={popupStats.active}
          icon="Play"
          color="green"
        />
        <MetricsCard
          title="Total Impressions"
          value={popupStats.totalImpressions.toLocaleString()}
          icon="Eye"
          color="purple"
        />
        <MetricsCard
          title="Avg Conversion"
          value={`${popupStats.avgConversionRate.toFixed(1)}%`}
          icon="TrendingUp"
          color="orange"
        />
      </div>

      {/* Create New Popup */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-6">
          Create New Pop-up
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              value={newPopup.title}
              onChange={(e) => setNewPopup(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter popup title"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              value={newPopup.type}
              onChange={(e) => setNewPopup(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="promotional">Promotional</option>
              <option value="informational">Informational</option>
              <option value="subscription">Subscription</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trigger
            </label>
            <select
              value={newPopup.trigger}
              onChange={(e) => setNewPopup(prev => ({ ...prev, trigger: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="page-load">Page Load</option>
              <option value="exit-intent">Exit Intent</option>
              <option value="time-based">Time Based</option>
              <option value="scroll-based">Scroll Based</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delay (ms)
            </label>
            <input
              type="number"
              value={newPopup.delay}
              onChange={(e) => setNewPopup(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="3000"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <textarea
              value={newPopup.content}
              onChange={(e) => setNewPopup(prev => ({ ...prev, content: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter popup content"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Call to Action Text
            </label>
            <input
              type="text"
              value={newPopup.cta}
              onChange={(e) => setNewPopup(prev => ({ ...prev, cta: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Learn More"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Pages
            </label>
            <select
              multiple
              value={newPopup.targetPages}
              onChange={(e) => setNewPopup(prev => ({ ...prev, targetPages: Array.from(e.target.selectedOptions, option => option.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="homepage">Homepage</option>
              <option value="vendor-listing">Vendor Listing</option>
              <option value="user-dashboard">User Dashboard</option>
              <option value="vendor-profile">Vendor Profile</option>
              <option value="all">All Pages</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-end mt-6 space-x-3">
          <Button variant="outline">Save as Draft</Button>
          <Button variant="primary" onClick={handleCreatePopup}>
            <Icon name="Plus" size={16} className="mr-2" />
            Create Pop-up
          </Button>
        </div>
      </div>

      {/* Popups List */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="p-6 border-b border-gray-200">
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Active Pop-ups
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Impressions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {popups.map((popup) => (
                <tr key={popup.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{popup.title}</div>
                      <div className="text-sm text-gray-500">{popup.content.substring(0, 50)}...</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(popup.type)}`}>
                      {popup.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(popup.status)}`}>
                      {popup.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {popup.impressions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {popup.conversions} ({((popup.conversions / popup.impressions) * 100).toFixed(1)}%)
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleStatusToggle(popup.id)}
                    >
                      <Icon name={popup.status === 'active' ? 'Pause' : 'Play'} size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="BarChart3" size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PopupManagerPanel;